import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

/**
 * Gateway — split-screen entry page.
 * Left  (linen / dark text): People & Enterprises, with a line-drawn cathedral.
 * Right (forest / light text): Machines, with an aerial tractor field.
 * Hovering one side dims the other so attention rests on the chosen path.
 */
const ART_LEFT  = "/brand/gateway/gateway-cathedral.webp";
const ART_RIGHT = "/brand/gateway/gateway-farm.webp";

export default function Gateway() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [hover, setHover] = useState(null); // "left" | "right" | null

  const clearTimer = React.useRef(null);
  const setSide = (s) => {
    if (clearTimer.current) { clearTimeout(clearTimer.current); clearTimer.current = null; }
    setHover(s);
  };
  const clearSide = (delayMs = 0) => {
    if (clearTimer.current) clearTimeout(clearTimer.current);
    if (delayMs === 0) setHover(null);
    else clearTimer.current = setTimeout(() => setHover(null), delayMs);
  };

  const goto = (path) => (e) => {
    e.preventDefault();
    nav(path);
  };

  const leftClasses  = ["cw-gw-panel", "cw-gw-panel-left",  hover === "left"  ? "is-active" : "", hover === "right" ? "is-dim" : ""].filter(Boolean).join(" ");
  const rightClasses = ["cw-gw-panel", "cw-gw-panel-right", hover === "right" ? "is-active" : "", hover === "left"  ? "is-dim" : ""].filter(Boolean).join(" ");

  return (
    <div data-testid="gateway-root" className={`cw-gw${hover ? " is-engaged" : ""}`}>
      {/* Brand lockup + campaign mark */}
      <div className="cw-gw-brand">
        <Link to="/coming-soon" data-testid="gateway-brand" className="cw-gw-brand-link" aria-label="Crowther">
          <img
            src="/brand/gateway/crowther-full-forest.webp"
            alt="Crowther"
            className="cw-gw-brand-logo"
          />
        </Link>
        <div className="cw-gw-campaign" data-testid="gateway-campaign" aria-label="AI for Humans">
          <span className="cw-gw-campaign-text">AI FOR HUMANS</span>
          <svg className="cw-gw-campaign-heart" width="14" height="12" viewBox="0 0 24 22" aria-hidden>
            <path d="M12 21s-9-5.5-9-12.5C3 4.4 6.4 2 9.5 2c1.9 0 3.6.9 4.5 2.3C14.9 2.9 16.6 2 18.5 2 21.6 2 25 4.4 25 8.5 25 15.5 12 21 12 21z"
                  transform="translate(-1,0)"
                  fill="#E63946" />
          </svg>
        </div>
      </div>

      {/* Panels */}
      <Link
        to="/enterprise"
        data-testid="gateway-left"
        aria-label="Enter for People & Enterprises"
        className={leftClasses}
        onMouseEnter={() => setSide("left")}
        onMouseLeave={() => clearSide(0)}
        onFocus={() => setSide("left")}
        onBlur={() => clearSide(0)}
        onPointerEnter={(e) => { if (e.pointerType !== "mouse") setSide("left"); }}
        onTouchStart={() => setSide("left")}
        onTouchEnd={() => clearSide(650)}
        onTouchCancel={() => clearSide(650)}
        onClick={goto("/enterprise")}
      >
        <img src={ART_LEFT} alt="" aria-hidden className="cw-gw-art cw-gw-art-left" />
        <div className="cw-gw-body cw-gw-body-left">
          <div className="cw-gw-tag cw-gw-tag-left">// FOR PEOPLE &amp; ENTERPRISES</div>
          <div className="cw-gw-num" data-testid="gateway-left-num">01</div>
          <p className="cw-gw-belief" data-testid="gateway-left-belief">
            AI shouldn&rsquo;t replace you.<br />
            It should be built for you.
          </p>
          <h1 className="cw-gw-headline" data-testid="gateway-left-headline">
            We are bringing intelligence to a billion people and enterprises.
          </h1>
          <p className="cw-gw-sub">
            Specialized intelligence built for your world.
          </p>
          <div className="cw-gw-cta" data-testid="gateway-left-cta">
            <span className="cw-gw-circle" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4">
                <path d="M2 7h9M8 4l3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="cw-gw-cta-text">ENTER THIS SIDE</span>
          </div>
        </div>
      </Link>

      <Link
        to="/machines"
        data-testid="gateway-right"
        aria-label="Enter for Machines"
        className={rightClasses}
        onMouseEnter={() => setSide("right")}
        onMouseLeave={() => clearSide(0)}
        onFocus={() => setSide("right")}
        onBlur={() => clearSide(0)}
        onPointerEnter={(e) => { if (e.pointerType !== "mouse") setSide("right"); }}
        onTouchStart={() => setSide("right")}
        onTouchEnd={() => clearSide(650)}
        onTouchCancel={() => clearSide(650)}
        onClick={goto("/machines")}
      >
        <img src={ART_RIGHT} alt="" aria-hidden className="cw-gw-art cw-gw-art-right" />
        <div className="cw-gw-body cw-gw-body-right">
          <div className="cw-gw-tag cw-gw-tag-right">// FOR MACHINES</div>
          <div className="cw-gw-num" data-testid="gateway-right-num">02</div>
          <p className="cw-gw-belief" data-testid="gateway-right-belief">
            Machines already support you.<br />
            We make them work better for you.
          </p>
          <h1 className="cw-gw-headline" data-testid="gateway-right-headline">
            We are bringing intelligence to a billion machines.
          </h1>
          <p className="cw-gw-sub">Physical AI for the machines that move your world.</p>
          <div className="cw-gw-cta" data-testid="gateway-right-cta">
            <span className="cw-gw-circle" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4">
                <path d="M2 7h9M8 4l3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="cw-gw-cta-text">ENTER THIS SIDE</span>
          </div>
        </div>
      </Link>

      {/* Footer tagline — split across the two sides */}
      <div className="cw-gw-footline cw-gw-footline-left"  data-testid="gateway-footline-left">HUMAN INSIGHT</div>
      <div className="cw-gw-footline cw-gw-footline-right" data-testid="gateway-footline-right">AMPLIFIED.</div>
    </div>
  );
}
